import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import useLocation from 'hooks/useLocation';
import { useMemo, useState } from 'react';
import { getApiErrorMessage } from 'services/_infra/apiError.utils';
import { useGetAirportConditionsbyStateQuery } from 'services/weather/airports/airports.api';
import {
  WeatherMapStaleDataNotice,
  WeatherMapUpdatingStatus,
} from '../../components/WeatherMapDataStatus/WeatherMapDataStatus';
import WeatherMapSegmentedControl from '../../components/WeatherMapSegmentedControl/WeatherMapSegmentedControl';
import '../../features/CurrentConditions/assets/currentConditions.scss';
import CurrentConditionsMapContent from '../../features/CurrentConditions/components/CurrentConditionsMapContent/CurrentConditionsMapContent';
import TemperatureLegend from '../../features/CurrentConditions/components/TemperatureLegend/TemperatureLegend';
import {
  CURRENT_CONDITIONS_QUERY_OPTIONS,
  CURRENT_CONDITIONS_REFRESH_INTERVAL_MINUTES,
  CURRENT_CONDITIONS_ROUTE,
} from '../../features/CurrentConditions/config/currentConditions.config';
import {
  getClosestCurrentCondition,
  getLatestCurrentConditionObservationTime,
  getMapReadyCurrentConditions,
} from '../../features/CurrentConditions/utils/currentConditions.utils';
import './assets/currentConditionsWidget.scss';
import CurrentConditionsStationSummary from './components/CurrentConditionsStationSummary/CurrentConditionsStationSummary';
import { CURRENT_CONDITIONS_WIDGET_VIEW_OPTIONS } from './config/currentConditionsWidgetView.config';
import WeatherMapWidgetEmptyState from '../components/WeatherMapWidgetEmptyState/WeatherMapWidgetEmptyState';
import WeatherMapWidgetLayout from '../components/WeatherMapWidgetLayout/WeatherMapWidgetLayout';
import { resolveWeatherMapWidgetLayout } from '../../utils/weatherMapWidgetLayout.utils';

const CurrentConditionsWidget = ({ layout, dimensions, containerSizePx }) => {
  const { locations } = useLocation();
  const [compactView, setCompactView] = useState('map');
  const {
    data: queryConditions = [],
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
    fulfilledTimeStamp,
  } = useGetAirportConditionsbyStateQuery(undefined, CURRENT_CONDITIONS_QUERY_OPTIONS);
  const conditions = Array.isArray(queryConditions) ? queryConditions : [];
  const center = locations.effectiveLocation?.coords;
  const mapReadyConditions = useMemo(() => getMapReadyCurrentConditions(conditions), [conditions]);
  const nearestCondition = useMemo(
    () => getClosestCurrentCondition(mapReadyConditions, center),
    [center, mapReadyConditions],
  );
  const displayedStation = nearestCondition ?? mapReadyConditions[0] ?? null;
  const latestObservationTime = useMemo(
    () => getLatestCurrentConditionObservationTime(mapReadyConditions),
    [mapReadyConditions],
  );
  const resolvedLayout = resolveWeatherMapWidgetLayout({
    layout,
    containerWidthPx: containerSizePx?.width ?? null,
  });
  const isCompactSinglePanel = resolvedLayout === '1x1';

  if (isLoading) {
    return <WidgetLoadingAnimation widgetName="MapForecast" dimensions={dimensions} />;
  }

  if (isError && !conditions.length) {
    return <ErrorMessage message={getApiErrorMessage(error)} onRetry={refetch} />;
  }

  if (!conditions.length || !mapReadyConditions.length) {
    const statusMessage = conditions.length
      ? 'Current conditions are available, but none include valid map coordinates.'
      : 'No current airport weather conditions are available right now.';

    return (
      <WeatherMapWidgetEmptyState
        ariaLabel="Current conditions widget"
        title="Current Conditions"
        message={statusMessage}
        className="current-conditions current-conditions-widget"
      />
    );
  }

  const map = (
    <CurrentConditionsMapContent center={center} conditions={mapReadyConditions} scrollZoom />
  );
  const stationSummary = (
    <CurrentConditionsStationSummary
      condition={displayedStation}
      isNearestStation={Boolean(nearestCondition)}
    />
  );
  const legend = <TemperatureLegend latestObservationTime={latestObservationTime} compact />;

  return (
    <section
      aria-label="Current conditions widget"
      className="current-conditions current-conditions-widget d-flex h-100 min-h-0 w-100 flex-column gap-1 overflow-hidden"
    >
      <WidgetTimeUpdate
        route={CURRENT_CONDITIONS_ROUTE}
        updateIntervalBasis={CURRENT_CONDITIONS_REFRESH_INTERVAL_MINUTES}
        compact
        updatedAt={fulfilledTimeStamp ?? null}
      />

      {isError && (
        <WeatherMapStaleDataNotice
          compact
          message="Update failed. Showing the most recently available conditions."
        />
      )}

      {isCompactSinglePanel ? (
        <div className="d-flex min-h-0 flex-grow-1 flex-column gap-2 overflow-hidden">
          <WeatherMapSegmentedControl
            ariaLabel="Current conditions widget view"
            options={CURRENT_CONDITIONS_WIDGET_VIEW_OPTIONS}
            value={compactView}
            onChange={setCompactView}
            className="current-conditions-widget-view-switch"
          />

          {compactView === 'map' ? (
            <div className="current-conditions-widget-map-view d-flex min-h-0 flex-grow-1 flex-column gap-1 overflow-hidden">
              <section aria-label="Current conditions map" className="d-flex min-h-0 flex-grow-1">
                {map}
              </section>
              <div className="flex-shrink-0 px-1">{legend}</div>
            </div>
          ) : (
            <section
              aria-label="Nearby current conditions"
              className="current-conditions-widget-information d-flex min-h-0 flex-grow-1 flex-column gap-2 overflow-auto rounded-2 border p-2"
              tabIndex={0}
            >
              {stationSummary}
              <div className="flex-shrink-0">{legend}</div>
            </section>
          )}
        </div>
      ) : (
        <WeatherMapWidgetLayout
          layout={resolvedLayout}
          mapAriaLabel="Current conditions map"
          informationAriaLabel="Nearby current conditions"
          mapClassName={
            resolvedLayout === '1x2' ? 'current-conditions-widget-map-stacked' : undefined
          }
          informationClassName={`current-conditions-widget-information flex-column gap-2 overflow-auto p-2 ${
            resolvedLayout === '1x2'
              ? 'current-conditions-widget-information-stacked'
              : 'current-conditions-widget-information-side'
          }`}
          informationTabIndex={0}
          mapContent={map}
          informationContent={
            <>
              {stationSummary}
              <div className="flex-shrink-0">{legend}</div>
            </>
          }
        />
      )}

      {isFetching && !isLoading && (
        <WeatherMapUpdatingStatus message="Updating current weather conditions" />
      )}
    </section>
  );
};

export default CurrentConditionsWidget;
